import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import FreePlanUpgradeModal from '../Modals/FreePlanUpgrade';

interface FreePlanUpgradeContextType {
    showModal: () => void;
    hideModal: () => void;
}

const FreePlanUpgradeContext = createContext<FreePlanUpgradeContextType | undefined>(undefined);

const LAST_SHOWN_KEY = 'free_plan_upgrade_modal_last_shown';

const shouldShowModal = (): boolean => {
    const lastShown = localStorage.getItem(LAST_SHOWN_KEY);
    if (!lastShown) return true;

    const lastShownDate = new Date(lastShown);
    const today = new Date();

    // Check if the last shown date is from a different day
    return lastShownDate.getDate() !== today.getDate() || lastShownDate.getMonth() !== today.getMonth() || lastShownDate.getFullYear() !== today.getFullYear();
};

const updateLastShown = () => {
    localStorage.setItem(LAST_SHOWN_KEY, new Date().toISOString());
};

export const useFreePlanUpgrade = () => {
    const context = useContext(FreePlanUpgradeContext);
    if (!context) {
        throw new Error('useFreePlanUpgrade must be used within a FreePlanUpgradeProvider');
    }
    return context;
};

interface FreePlanUpgradeProviderProps {
    children: ReactNode;
    isFreePlan: boolean;
}

export const FreePlanUpgradeProvider: React.FC<FreePlanUpgradeProviderProps> = ({ children, isFreePlan }) => {
    const [isVisible, setIsVisible] = useState(false);
    const location = useLocation();

    const showModal = () => {
        setIsVisible(true);
        updateLastShown();
    };

    const hideModal = () => setIsVisible(false);

    // Show modal when user is on overview page, has free plan, and hasn't seen it today
    useEffect(() => {
        if (isFreePlan && location.pathname === '/overview' && shouldShowModal()) {
            const timer = setTimeout(() => {
                showModal();
            }, 5000); // 5 seconds delay

            return () => clearTimeout(timer); // Cleanup the timer if component unmounts or dependencies change
        }
    }, [location.pathname, isFreePlan]);

    return (
        <FreePlanUpgradeContext.Provider value={{ showModal, hideModal }}>
            {children}
            <FreePlanUpgradeModal visible={isVisible} onClose={hideModal} />
        </FreePlanUpgradeContext.Provider>
    );
};
